import '@/global.css'
import { Input } from "@/components/ui/input"
import { Link } from "react-router";
import { ROUTE } from '@/routes/constants'
import { LoadingOverlay } from '@/components/ui/loading-overlay'
import { ThemeColors } from './login-theme'
import { EmailIcon, LockIcon, EyeIcon, LoginArrowIcon } from './login-icons'
import { useLogin } from './use-login'

interface MobileLoginFormProps {
  colors: ThemeColors;
}

// 移动端登录表单：与 PC 端共用 useLogin（接口/校验逻辑一致），仅布局改为流式自适应。
// 视觉沿用设计稿样式（同款图标、配色、圆角胶囊输入框），尺寸按手机屏等比缩小。
function MobileLoginForm({ colors }: MobileLoginFormProps) {
  const {
    t, loginMessage, isLoading, isEmailInvalid, isPswInvalid,
    showPassword, setShowPassword, setEmailInvalid, setPswInvalid, onLogin,
  } = useLogin();

  const labelStyle: React.CSSProperties = { fontSize: 14, fontWeight: 500, color: '#575C60', lineHeight: '18px' };
  const inputStyle: React.CSSProperties = {
    width: '100%', height: 52, borderRadius: 26, background: colors.inputBg, border: 0,
    fontSize: 15, color: '#1F1F1F',
  };

  return (
    <form action={onLogin} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <LoadingOverlay isLoading={isLoading} />

      {/* 标题 + 副标题 */}
      <h2 style={{ margin: 0, textAlign: 'center', fontSize: 28, fontWeight: 600, lineHeight: '34px', color: '#2A2F32', letterSpacing: '-0.01em' }}>
        {t("user.welcome-back")}
      </h2>
      <p style={{ marginTop: 8, marginBottom: 0, textAlign: 'center', fontSize: 14, fontWeight: 400, color: '#575C60', lineHeight: '20px' }}>
        {t("user.welcome-sub")}
      </p>
      {loginMessage && (
        <p style={{ marginTop: 8, marginBottom: 0, textAlign: 'center', fontSize: 13, color: '#EF4444', lineHeight: '18px' }}>{loginMessage}</p>
      )}

      {/* 電子郵箱 */}
      <label htmlFor="email" style={{ ...labelStyle, marginTop: 28 }}>{t("user.email")}</label>
      <div style={{ position: 'relative', marginTop: 8, height: 52 }}>
        <EmailIcon aria-hidden="true" style={{ position: 'absolute', left: 20, top: 15, width: 22, height: 22, zIndex: 1, color: '#575C60' }} />
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="scholar@atelier.edu.hk"
          required
          onInvalid={(e: any) => { setEmailInvalid(true); e.target.setCustomValidity(t("user.email-error")); }}
          onInput={(e: any) => { e.target.setCustomValidity(""); }}
          className="pl-12 pr-4 placeholder:text-[rgba(115,119,123,0.5)] focus-visible:ring-2"
          style={{ ...inputStyle, outline: isEmailInvalid ? '2px solid #F87171' : undefined, ['--tw-ring-color' as any]: colors.link + '66' }}
        />
      </div>

      {/* 密碼 + 忘記密碼 */}
      <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <label htmlFor="password" style={labelStyle}>{t("user.password")}</label>
        <Link
          to={ROUTE.REQUEST_RESET_PASSWORD}
          style={{ fontSize: 13, fontWeight: 400, color: colors.link, lineHeight: '16px' }}
          className="hover:underline"
        >
          {t("user.forgot-password")}?
        </Link>
      </div>
      <div style={{ position: 'relative', marginTop: 8, height: 52 }}>
        <LockIcon aria-hidden="true" style={{ position: 'absolute', left: 23, top: 16, width: 16, height: 21, zIndex: 1, color: '#575C60' }} />
        <Input
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          required
          pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
          onInvalid={(e: any) => { setPswInvalid(true); e.target.setCustomValidity(t("user.password-error")); }}
          onInput={(e: any) => { e.target.setCustomValidity(""); }}
          className="pl-12 pr-12 placeholder:text-[rgba(115,119,123,0.5)] focus-visible:ring-2"
          style={{ ...inputStyle, outline: isPswInvalid ? '2px solid #F87171' : undefined, ['--tw-ring-color' as any]: colors.link + '66' }}
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          style={{ position: 'absolute', right: 18, top: 19, width: 22, height: 15 }}
        >
          <EyeIcon aria-hidden="true" style={{ width: 22, height: 15, color: '#73777B' }} />
        </button>
      </div>

      {/* 登录按钮 */}
      <button
        type="submit"
        style={{
          marginTop: 24, width: '100%', height: 56, borderRadius: 28,
          background: colors.buttonBg, color: colors.buttonText,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontSize: 18, fontWeight: 500, border: 0, cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
        className="transition-[filter] hover:brightness-[0.97]"
      >
        {t("user.sign-in-action")}
        <LoginArrowIcon aria-hidden="true" style={{ width: 18, height: 18, color: colors.buttonText }} />
      </button>

      {/* 分隔线 */}
      <div style={{ marginTop: 28, width: '100%', height: 1, background: '#E5E7EB' }} />

      {/* 注册引导 */}
      <div style={{ marginTop: 20, width: '100%', textAlign: 'center', fontSize: 14, color: '#575C60', lineHeight: '22px' }}>
        {t("user.login-comment")}
        <Link to={ROUTE.SIGNUP} style={{ marginLeft: 6, fontWeight: 500, color: colors.link }} className="underline underline-offset-4">
          {t("user.sign-up")}
        </Link>
      </div>
    </form>
  );
}

export default MobileLoginForm;
